import useSWR from 'swr';
import { swrFetcher } from 'contexts/user';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import usePermission from 'components/profile/usePermission';
import { ValueCallback } from 'types/callbacks';
import { updateProfile } from 'lib/api/owner_api';
import { ProfileResponse } from 'components/login/types';
import AccountField from './AccountField';
import SummaryField from './SummaryField';
import {
  getUpdateProfileErrorCaption,
  UpdateProfileError,
  UpdateProfileErrorField,
} from 'types/profile';
import AccountDivider from './AccountDivider';
import {
  ChangePasswordButton,
  EditProfileButton,
  ResendEmailVerification,
  DeleteAccountButton,
} from './AccountActions';
import {
  ContentArea,
  LeadingArea,
  TrailingArea,
  WideContentArea,
} from './AccountLayout';
import styles from 'components/account/account.module.css';
import { AccountToggle } from './AccountToggle';

function getErrorCaption(
  errors: Record<UpdateProfileErrorField, UpdateProfileError>
): string {
  if (errors.password) {
    return `Password ${getUpdateProfileErrorCaption(errors.password)}`;
  }

  if (errors.username) {
    return `Username ${getUpdateProfileErrorCaption(errors.username)}`;
  }

  if (errors.email) {
    return `Email ${getUpdateProfileErrorCaption(errors.email)}`;
  }

  return 'Something went wrong, please try again';
}

interface Props {
  id?: number;
}

const OwnerSummary = ({ id }: Props) => {
  const isUserProfile = id === undefined;
  const endpoint = id ? `/api/teams/${id}` : '/api/profile';
  const { isAdmin } = usePermission(id);
  const { data: profile } = useSWR<ProfileResponse>(endpoint, swrFetcher);
  const [isEditing, setEditing] = useState<boolean>(false);

  return (
    <>
      <AccountDivider />
      <LeadingArea>
        {isUserProfile && <h2 data-testid="owner-label">Profile</h2>}
      </LeadingArea>
      {isEditing && profile ? (
        <ProfileForm
          id={id}
          profile={profile}
          isEditing={isEditing}
          setEditing={setEditing}
        />
      ) : (
        <>
          <ContentArea>
            {isUserProfile && (
              <SummaryField label="Email" value={profile?.email} />
            )}
            <SummaryField label="Username" value={profile?.username} />
            <SummaryField label="Display Name" value={profile?.name} />
            {(isUserProfile || profile?.location) && (
              <SummaryField label="Location" value={profile?.location} />
            )}
            {(isUserProfile || profile?.bio) && (
              <SummaryField label="Bio" value={profile?.bio} />
            )}
            {(isUserProfile || profile?.website) && (
              <SummaryField label="Website" value={profile?.website} />
            )}
            {profile && <Socials {...profile} />}
            {isUserProfile && profile && <ForHireToggle profile={profile} />}
          </ContentArea>
          <TrailingArea>
            {(isAdmin || isUserProfile) && (
              <EditProfileButton onClick={() => setEditing(true)} />
            )}
            {isUserProfile && <ChangePasswordButton />}
            <ResendEmailVerification />
            {isUserProfile && <DeleteAccountButton />}
          </TrailingArea>
        </>
      )}
    </>
  );
};

export default OwnerSummary;

interface FormProps {
  id?: number;
  profile: ProfileResponse;
  isEditing: boolean;
  setEditing: ValueCallback<boolean>;
}

const ProfileForm = ({ id, profile, isEditing, setEditing }: FormProps) => {
  const isUserProfile = id === undefined;
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { register, reset, handleSubmit, formState } = useForm({
    mode: 'onChange',
    defaultValues: { ...profile },
  });

  useEffect(() => {
    reset({ ...profile });
    setError(null);
  }, [profile, isEditing]);

  async function update(data: any) {
    setError(null);
    setSubmitting(true);
    const { errors } = await updateProfile(data, id);
    if (errors) {
      const caption = getErrorCaption(errors);
      setError(caption);
    } else {
      reset({ ...profile });
      setEditing(false);
    }
    setSubmitting(false);
  }

  return (
    <WideContentArea>
      <form onSubmit={handleSubmit(update)}>
        {isUserProfile && (
          <AccountField
            label="Email"
            register={register('email', { required: true })}
          />
        )}
        <AccountField label="Username" register={register('username')} />
        <AccountField label="Display Name" register={register('name')} />
        <AccountField label="Location" register={register('location')} />
        <AccountField
          label="Bio"
          register={register('bio', {
            maxLength: {
              value: 160,
              message: 'Caption must be less that 160 characters.',
            },
          })}
          errorMessage={formState.errors.bio?.message as string}
        />
        <AccountField label="Website" register={register('website')} />
        <AccountField label="X" register={register('twitter')} />
        <AccountField label="Instagram" register={register('instagram')} />
        <AccountField label="LinkedIn" register={register('linkedin')} />
        <AccountField label="GitHub" register={register('github')} />
        <AccountField label="Behance" register={register('behance')} />
        <AccountField label="Dribbble" register={register('dribbble')} />
        <AccountField label="Medium" register={register('medium')} />
        <AccountField label="Vimeo" register={register('vimeo')} />
        <div className={styles.InlineFormActionContainer}>
          <p className={styles.Error} style={{ margin: 0 }}>
            {error}
          </p>
          {isSubmitting ? (
            <p style={{ marginBottom: '16px' }}>Submitting...</p>
          ) : (
            <>
              <button
                type="button"
                className={styles.ContextAction}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <button type="submit" className={styles.ContextAction}>
                Save changes
              </button>
            </>
          )}
        </div>
      </form>
    </WideContentArea>
  );
};

interface SocialsProps {
  twitter?: string | null;
  instagram?: string | null;
  linkedin?: string | null;
  github?: string | null;
  behance?: string | null;
  dribbble?: string | null;
  medium?: string | null;
  vimeo?: string | null;
}

const Socials = ({
  twitter,
  instagram,
  linkedin,
  github,
  behance,
  dribbble,
  medium,
  vimeo,
}: SocialsProps) => (
  <>
    {twitter && <SummaryField label="X" value={twitter} />}
    {instagram && <SummaryField label="Instagram" value={instagram} />}
    {linkedin && <SummaryField label="LinkedIn" value={linkedin} />}
    {github && <SummaryField label="GitHub" value={github} />}
    {behance && <SummaryField label="Behance" value={behance} />}
    {dribbble && <SummaryField label="Dribbble" value={dribbble} />}
    {medium && <SummaryField label="Medium" value={medium} />}
    {vimeo && <SummaryField label="Vimeo" value={vimeo} />}
  </>
);

interface ForHireProps {
  profile: ProfileResponse;
}

const ForHireToggle = ({ profile }: ForHireProps) => {
  const [isForHire, setForHire] = useState<boolean>(profile.isForHire);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  async function update() {
    setSubmitting(true);
    setForHire(!isForHire);
    await updateProfile({
      ...profile,
      isForHire: !isForHire,
    });
    setSubmitting(false);
  }

  return (
    <>
      <AccountToggle
        label="Available For Hire: "
        onToggle={update}
        isChecked={isForHire || isSubmitting}
        checkColor={isSubmitting ? '#444' : '#f1f1f1'}
      />
      {isForHire && (
        <p>
          Edit your profile to link social accounts and/or let others know how
          best to contact you via your bio.
        </p>
      )}
    </>
  );
};
